// #51 Quote, Standard, Descriptor and Evidence: the reading blocks of the scorecard and the summary.
// Quote is a quoted passage on the secondary surface with the purple rule, optionally attributed to a person; `plain`
// is the light pull line (.mc-q: the --text-2 line on a hairline rule) used for an evidence excerpt. Standard is the
// guide's standard for the section; Descriptor is the chosen number's descriptor under its blue rule; Evidence is the
// blue-tinted block titled *Evidence* (or *Rationale*) that holds what the interviewer wrote.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { useSvgId } from "../lib/useSvgId";
import { Avatar, type AvatarProps } from "../core/Person";
import { SCORECARD_LABELS } from "./labels";

export interface QuoteProps {
  children: ReactNode;
  /** Who wrote it: the name, and their hue key. */
  who?: Pick<AvatarProps, "name" | "hueKey" | "hue"> | undefined;
  /** Beside the name (their seat, the time). */
  sub?: ReactNode | undefined;
  /** The light pull line (.mc-q) rather than the boxed quote. */
  plain?: boolean | undefined;
  className?: string | undefined;
}

export function Quote({ children, who, sub, plain, className }: QuoteProps) {
  if (plain) return <blockquote className={cx("mc-q", className)}>{children}</blockquote>;
  return (
    <figure className={cx("mc-quote", className)}>
      {who ? (
        <figcaption className="mc-quote-who">
          <Avatar {...who} size={24} decorative />
          <b>{who.name}</b>
          {sub !== undefined && sub !== null ? <span className="mc-t3">{sub}</span> : null}
        </figcaption>
      ) : null}
      <blockquote className="mc-quote-b">{children}</blockquote>
    </figure>
  );
}

export interface StandardProps {
  children: ReactNode;
  className?: string | undefined;
}

export function Standard({ children, className }: StandardProps) {
  return <p className={cx("mc-std", className)}>{children}</p>;
}

export function Descriptor({ children, className }: StandardProps) {
  return <p className={cx("mc-desc", className)}>{children}</p>;
}

export interface EvidenceProps {
  children: ReactNode;
  /** The block's title: *Evidence* (default) or *Rationale*. */
  title?: string | undefined;
  className?: string | undefined;
}

export function Evidence({ children, title = SCORECARD_LABELS.evidence, className }: EvidenceProps) {
  const id = `${useSvgId()}-evid`;
  return (
    <section className={cx("mc-evid", className)} aria-labelledby={id}>
      <h4 className="mc-evid-t" id={id}>{title}</h4>
      <div className="mc-evid-b">{children}</div>
    </section>
  );
}
